import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from './local.storage.service';
import { UserService } from './user.service';
import { CABINET_TYPE } from '../constants/platform-type.constants';
import { PlatformType } from '@data-access';

export const LANGUAGE_STORAGE_KEY = 'language';
export const SUPPORTED_LANGUAGES = ['en', 'es'] as const;
export const DEFAULT_LANGUAGE = 'en';

/** The only filter on a language value: anything unsupported reads as "not set". */
const normalize = (value: string | null | undefined): string | null =>
  value && (SUPPORTED_LANGUAGES as readonly string[]).includes(value) ? value : null;

/**
 * The UI language, resolved once and in one place.
 *
 * Precedence: the user's own choice, then the language an admin set on their
 * carrier/customer, then English. The browser is deliberately not consulted — it must
 * not decide a date format on its own.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translateService = inject(TranslateService);
  private readonly storage = inject(LocalStorageService);
  private readonly userService = inject(UserService);
  private readonly cabinetType = inject(CABINET_TYPE);

  private readonly storedLanguage = signal(normalize(this.storage.get(LANGUAGE_STORAGE_KEY)));

  // TODO: #1012 Fix after multi-cabinet implemented
  private readonly profileLanguage = computed(() => {
    // An admin has no carrier or customer to carry a localization, so there is nothing to
    // read: their language is whatever they picked, and English until they pick.
    if (this.cabinetType === PlatformType.WebAdmin) return null;

    const user = this.userService.currentUser();

    const language =
      this.cabinetType === PlatformType.WebCustomer
        ? user?.access?.customers?.[0]?.localization?.language
        : user?.access?.carriers?.[0]?.localization?.language;

    return normalize(language?.toLowerCase());
  });

  /**
   * The language somebody actually chose — through the switcher, or an admin on the
   * cabinet. `null` when nobody has, which is the whole difference from
   * `currentLanguage`: an unset language means the default locale, not a guessed one.
   */
  readonly selectedLanguage = computed(() => this.storedLanguage() ?? this.profileLanguage());

  /** What the UI text is rendered in. */
  readonly currentLanguage = computed(() => this.selectedLanguage() ?? DEFAULT_LANGUAGE);

  constructor() {
    // Applied straight away rather than only from the effect below: this service is
    // built during app initialization, and an effect would not run until the first
    // change detection — after the first template has already asked for its strings.
    this.translateService.use(this.currentLanguage());

    // The profile arrives through the store, so a cabinet whose language an admin
    // changed switches over without anyone calling anything.
    effect(() => this.translateService.use(this.currentLanguage()));
  }

  setLanguage(lang: string): void {
    const language = normalize(lang) ?? DEFAULT_LANGUAGE;

    this.storage.set(LANGUAGE_STORAGE_KEY, language);
    this.storedLanguage.set(language);
  }
}
